import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
function isAxiosResponse(res) {
    return (typeof res === "object" &&
        res !== null &&
        "status" in res &&
        "data" in res);
}
// Async thunk for fetching holidays
export const fetchHolidays = createAsyncThunk("userData/fetchHolidays", async (args, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const url = args && args.month ? `/api/holidays?month=${args.month}` : `/api/holidays`;
        const response = await ApiCall("GET", url, "", {
            Authorization: token || ""
        });
        if (response && 'data' in response && response?.data) {
            return response?.data?.data?.holidays;
        }
        else {
            return rejectWithValue("Failed to fetch holidays");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data || error.message || "An error occurred");
    }
});
// Async thunk for fetching modules
export const fetchModules = createAsyncThunk("userData/fetchModules", async (_, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/modules", "", {
            Authorization: token || ""
        });
        if (isAxiosResponse(response) && response.status === 200) {
            return response.data?.data;
        }
        else {
            return rejectWithValue("Failed to fetch modules");
        }
    }
    catch (error) {
        console.error("Modules API Error:", error);
        return rejectWithValue(error?.response?.data || error.message || "An error occurred");
    }
});
// Async thunk for checking email existence
export const checkUserEmailExists = createAsyncThunk("userData/checkUserEmailExists", async ({ email, _id }, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const params = new URLSearchParams();
        if (_id)
            params.append('_id', _id);
        const response = await ApiCall("GET", `/api/users/check-email/${encodeURIComponent(email)}${params.toString() ? `?${params.toString()}` : ""}`, "", { Authorization: token || "" });
        if (isAxiosResponse(response) && response.status === 200) {
            return response.data?.data;
        }
        else {
            return rejectWithValue("Failed to check email");
        }
    }
    catch (error) {
        console.error("Email Check API Error:", error);
        return rejectWithValue(error?.response?.data || error.message || "An error occurred");
    }
});
const initialState = {
    holidays: [],
    holidaysLoading: false,
    holidaysError: null,
    modules: [],
    modulesLoading: false,
    modulesError: null,
    modulesLoaded: false,
    activeModuleKey: null,
    permissionDenied: false,
    permissionDeniedMessage: null,
};
const userDataSlice = createSlice({
    name: "userData",
    initialState,
    reducers: {
        clearHolidaysError: (state) => {
            state.holidaysError = null;
        },
        clearHolidaysData: (state) => {
            state.holidays = [];
            state.holidaysError = null;
        },
        setActiveModuleKey: (state, action) => {
            state.activeModuleKey = action.payload;
        },
        clearActiveModuleKey: (state) => {
            state.activeModuleKey = null;
        },
        removeModuleByKeySafe: (state, action) => {
            const keyToUpdate = action.payload;
            if (!keyToUpdate)
                return;
            const updateRecursively = (modules) => {
                modules.forEach(module => {
                    // case 1: exact match → access false
                    if (module.key === keyToUpdate) {
                        if (!module.permissions) {
                            module.permissions = { access: false };
                        }
                        else {
                            module.permissions.access = false;
                        }
                    }
                    // traverse children
                    if (module.sub_modules?.length) {
                        updateRecursively(module.sub_modules);
                        // case 2: parent but all children inactive → parent access false
                        if (module.is_parent &&
                            module.sub_modules.every(child => child.permissions?.access === false)) {
                            if (!module.permissions) {
                                module.permissions = { access: false };
                            }
                            else {
                                module.permissions.access = false;
                            }
                        }
                    }
                });
            };
            updateRecursively(state.modules);
        },
        setPermissionDenied: (state, action) => {
            state.permissionDenied = true;
            state.permissionDeniedMessage = action.payload.message;
        },
        clearPermissionDenied: (state) => {
            state.permissionDenied = false;
            state.permissionDeniedMessage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHolidays.pending, (state) => {
            state.holidaysLoading = true;
            state.holidaysError = null;
        })
            .addCase(fetchHolidays.fulfilled, (state, action) => {
            state.holidaysLoading = false;
            state.holidays = action.payload;
            state.holidaysError = null;
        })
            .addCase(fetchHolidays.rejected, (state, action) => {
            state.holidaysLoading = false;
            state.holidaysError = action.payload || null;
        })
            .addCase(fetchModules.pending, (state) => {
            state.modulesLoading = true;
            state.modulesError = null;
        })
            .addCase(fetchModules.fulfilled, (state, action) => {
            state.modulesLoading = false;
            state.modules = action.payload;
            state.modulesError = null;
            state.modulesLoaded = true;
        })
            .addCase(fetchModules.rejected, (state, action) => {
            state.modulesLoading = false;
            state.modulesError = action.payload || null;
            state.modulesLoaded = false;
        });
    },
});
export const { clearHolidaysError, clearHolidaysData, setActiveModuleKey, clearActiveModuleKey, removeModuleByKeySafe, setPermissionDenied, clearPermissionDenied } = userDataSlice.actions;
// Selectors
export const selectHolidays = (state) => state.userData.holidays;
export const selectHolidaysLoading = (state) => state.userData.holidaysLoading;
export const selectHolidaysError = (state) => state.userData.holidaysError;
export const selectModules = (state) => state.userData.modules;
export const selectModulesLoading = (state) => state.userData.modulesLoading;
export const selectModulesError = (state) => state.userData.modulesError;
export const selectModulesLoaded = (state) => state.userData.modulesLoaded;
export const selectActiveModuleKey = (state) => state.userData.activeModuleKey;
export const selectPermissionDenied = (state) => state.userData.permissionDenied;
export const selectPermissionDeniedMessage = (state) => state.userData.permissionDeniedMessage;
export default userDataSlice.reducer;
